import { z } from "zod";

/**
 * Psychiatry assessment — PHQ-9 (depression) + GAD-7 (anxiety) validated
 * screeners + mental status / risk / plan. Total scores + severity bands are
 * derived deterministically; the self-harm flag is derived from PHQ-9 item 9.
 */

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
const item = z.coerce.number().int().min(0).max(3);
const phq9Items = z.array(item).length(9);
const gad7Items = z.array(item).length(7);

export const psychAssessmentApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	assessment_date: dateStr,
	phq9_items: z.array(z.number().int()).nullable(),
	phq9_score: z.number().int().nullable(),
	phq9_severity: z.string().nullable(),
	gad7_items: z.array(z.number().int()).nullable(),
	gad7_score: z.number().int().nullable(),
	gad7_severity: z.string().nullable(),
	self_harm_flag: z.boolean().nullable(),
	mental_status: z.string().nullable(),
	risk_notes: z.string().nullable(),
	assessment: z.string().nullable(),
	plan: z.string().nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type PsychAssessmentApi = z.infer<typeof psychAssessmentApiSchema>;

export const createPsychAssessmentInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	assessment_date: dateStr,
	phq9_items: phq9Items.nullable().optional(),
	gad7_items: gad7Items.nullable().optional(),
	mental_status: z.string().max(2000).nullable().optional(),
	risk_notes: z.string().max(2000).nullable().optional(),
	assessment: z.string().max(2000).nullable().optional(),
	plan: z.string().max(2000).nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreatePsychAssessmentInput = z.infer<
	typeof createPsychAssessmentInputSchema
>;

// ===== Deterministic PHQ-9 scoring =====

export type Phq9Severity =
	| "minimal"
	| "mild"
	| "moderate"
	| "moderately_severe"
	| "severe";

export interface Phq9Result {
	score: number;
	severity: Phq9Severity;
	/** PHQ-9 item 9 (suicidal ideation) > 0. */
	selfHarmFlag: boolean;
}

/** PHQ-9: sum of 9 items (0–3 each → 0–27) + standard severity cut-offs. */
export function scorePhq9(
	items: Array<number | null | undefined> | null | undefined,
): Phq9Result | null {
	if (!items || items.length !== 9) return null;
	if (items.some((v) => v == null || !Number.isFinite(v))) return null;
	const vals = items as number[];
	const score = vals.reduce((a, b) => a + b, 0);
	let severity: Phq9Severity;
	if (score <= 4) severity = "minimal";
	else if (score <= 9) severity = "mild";
	else if (score <= 14) severity = "moderate";
	else if (score <= 19) severity = "moderately_severe";
	else severity = "severe";
	return { score, severity, selfHarmFlag: (vals[8] ?? 0) > 0 };
}

// ===== Deterministic GAD-7 scoring =====

export type Gad7Severity = "minimal" | "mild" | "moderate" | "severe";

export interface Gad7Result {
	score: number;
	severity: Gad7Severity;
}

/** GAD-7: sum of 7 items (0–3 each → 0–21) + standard severity cut-offs. */
export function scoreGad7(
	items: Array<number | null | undefined> | null | undefined,
): Gad7Result | null {
	if (!items || items.length !== 7) return null;
	if (items.some((v) => v == null || !Number.isFinite(v))) return null;
	const vals = items as number[];
	const score = vals.reduce((a, b) => a + b, 0);
	let severity: Gad7Severity;
	if (score <= 4) severity = "minimal";
	else if (score <= 9) severity = "mild";
	else if (score <= 14) severity = "moderate";
	else severity = "severe";
	return { score, severity };
}
